
import React, { useState, useEffect } from 'react';

interface WaitlistPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WaitlistPopup({
  isOpen,
  onClose,
}: WaitlistPopupProps) {
  const [step, setStep] = useState<'waitlist' | 'survey' | 'complete'>(
    'waitlist'
  );
  const [email, setEmail] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [automation, setAutomation] = useState('');
  const [stage, setStage] = useState('');

  // Safely read URL params only in the browser
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const source = params.get('utm_source') ?? '';
      setUtmSource(source);
    }
  }, []);

  // Lock scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const automationOptions = [
    'Managing different portfolios in one place (Reels, Images, Writeups)',
    'Making my social media link look professional',
    'Chasing clients for project reviews & inquiry details',
  ];

  const stageOptions = [
    '1-3 projects/Need first big client (1-3 projects completed)',
    'Established but messy (back office needs help)',
    'Starting out/Student',
  ];

  const handleWaitlistSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('utm_source', utmSource);

      const response = await fetch(
        'https://readdy.ai/api/form/d6014jckjhtioh5jsdig',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
        }
      );

      if (response.ok) {
        setStep('survey');
        setMessage('');
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSurveySubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    if (!automation || !stage) {
      setMessage('Please answer both questions');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('automation_priority', automation);
      formData.append('current_stage', stage);
      formData.append('utm_source', utmSource);

      const response = await fetch(
        'https://readdy.ai/api/form/d6014jckjhtioh5jsdi0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
        }
      );

      if (response.ok) {
        setStep('complete');
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setStep('waitlist');
    setEmail('');
    setAutomation('');
    setStage('');
    setMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div
        className="absolute inset-0 bg-[#2F3438]/50 backdrop-blur-sm"
        onClick={resetAndClose}
      ></div>

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <button
          onClick={resetAndClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg bg-[#ABB2B9]/10 hover:bg-[#ABB2B9]/20 transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-base sm:text-lg text-[#2F3438]"></i>
        </button>

        {step === 'waitlist' && (
          <div className="p-6 sm:p-8 lg:p-10">
            <div className="text-center mb-6 sm:mb-8">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center mx-auto mb-3 sm:mb-4 bg-[#35C5F0] rounded-xl">
                <i className="ri-mail-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#2F3438] mb-2 sm:mb-3">
                Join the Waitlist
              </h2>
              <p className="text-sm sm:text-base text-[#ABB2B9]">
                Be among the first to get your free forever link
              </p>
            </div>

            <form
              onSubmit={handleWaitlistSubmit}
              className="space-y-4"
              data-readdy-form
              id="waitlist-popup-form-beta"
            >
              <input type="hidden" name="utm_source" value={utmSource} />

              <div>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 text-sm border border-[#ABB2B9]/30 rounded-lg focus:outline-none focus:border-[#35C5F0] focus:ring-2 focus:ring-[#35C5F0]/20 transition-all bg-white text-[#2F3438]"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-[#35C5F0] text-white text-sm font-medium rounded-lg hover:bg-[#2bb5de] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Joining...' : 'Join the Waitlist'}
              </button>

              {message && (
                <p
                  className={`text-center text-xs sm:text-sm ${
                    message.includes('Success')
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {message}
                </p>
              )}
            </form>

            <p className="mt-4 sm:mt-6 text-center text-xs sm:text-sm text-[#ABB2B9]">
              Free forever. No credit card required.
            </p>
          </div>
        )}

        {step === 'survey' && (
          <div className="p-6 sm:p-8 lg:p-10">
            <div className="text-center mb-6 sm:mb-8">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center mx-auto mb-3 sm:mb-4 bg-green-100 rounded-xl">
                <i className="ri-check-line text-xl sm:text-2xl text-green-600"></i>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#2F3438] mb-2 sm:mb-3">
                Thank You!
              </h2>
              <p className="text-sm sm:text-base text-[#ABB2B9]">
                Help us tailor the perfect experience for you
              </p>
            </div>

            <form
              onSubmit={handleSurveySubmit}
              className="space-y-6 sm:space-y-8"
              data-readdy-form
              id="survey-popup-form-beta"
            >
              <input type="hidden" name="email" value={email} />
              <input type="hidden" name="utm_source" value={utmSource} />

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-[#2F3438] mb-3 sm:mb-4">
                  What's the one thing you want to automate first?
                </label>
                <div className="space-y-2">
                  {automationOptions.map((option) => (
                    <label
                      key={option}
                      className={`flex items-center p-3 sm:p-4 border rounded-lg cursor-pointer transition-all ${
                        automation === option
                          ? 'border-[#35C5F0] bg-[#35C5F0]/5'
                          : 'border-[#ABB2B9]/30 hover:border-[#35C5F0]/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="automation_priority"
                        value={option}
                        checked={automation === option}
                        onChange={(e) => setAutomation(e.target.value)}
                        className="w-4 h-4 text-[#35C5F0] cursor-pointer accent-[#35C5F0] flex-shrink-0"
                      />
                      <span className="ml-3 text-xs sm:text-sm text-[#2F3438]">
                        {option}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-[#2F3438] mb-3 sm:mb-4">
                  How would you describe your current stage?
                </label>
                <div className="space-y-2">
                  {stageOptions.map((option) => (
                    <label
                      key={option}
                      className={`flex items-center p-3 sm:p-4 border rounded-lg cursor-pointer transition-all ${
                        stage === option
                          ? 'border-[#35C5F0] bg-[#35C5F0]/5'
                          : 'border-[#ABB2B9]/30 hover:border-[#35C5F0]/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="current_stage"
                        value={option}
                        checked={stage === option}
                        onChange={(e) => setStage(e.target.value)}
                        className="w-4 h-4 text-[#35C5F0] cursor-pointer accent-[#35C5F0] flex-shrink-0"
                      />
                      <span className="ml-3 text-xs sm:text-sm text-[#2F3438]">
                        {option}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-[#35C5F0] text-white text-sm font-medium rounded-lg hover:bg-[#2bb5de] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Survey'}
              </button>

              {message && (
                <p
                  className={`text-center text-xs sm:text-sm ${
                    message.includes('Thank you')
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {message}
                </p>
              )}
            </form>
          </div>
        )}

        {step === 'complete' && (
          <div className="p-6 sm:p-8 lg:p-10 text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 sm:mb-6 bg-[#35C5F0]/10 rounded-xl">
              <i className="ri-checkbox-circle-fill text-3xl sm:text-4xl text-[#35C5F0]"></i>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#2F3438] mb-3 sm:mb-4">
              You're All Set!
            </h2>
            <p className="text-sm sm:text-base text-[#ABB2B9] mb-6 sm:mb-8 leading-relaxed">
              We'll notify you as soon as your free forever link is ready.
              Get ready to shine!
            </p>
            <button
              onClick={resetAndClose}
              className="px-6 sm:px-8 py-3 bg-[#35C5F0] text-white text-sm font-medium rounded-lg hover:bg-[#2bb5de] transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Continue Exploring
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
